import { AuthStorage } from "utils/auth-storage";
import { SwapOutlined } from "@ant-design/icons";
import { Empty, Pagination, Select, Table, Tooltip } from "antd";
import React, { useEffect, useMemo, useRef, useState } from "react";

import { SideBarEndService } from "apis/Services/SideBarService";
import LoadingComponent from "components/loading-component";
import styles from "./table.module.css";

function getInitials(fullName = "") {
  const parts = String(fullName).trim().split(/\s+/).filter(Boolean);

  if (!parts.length) {
    return "?";
  }

  return parts
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join("");
}

function formatNotificationTimestamp(dateTimeString = "") {
  if (!dateTimeString) {
    return {
      formattedDate: "Unknown",
      formattedTime: "",
      timeZoneName: "",
    };
  }

  const [datePart = "", timePart = ""] = String(dateTimeString).split(" ");
  const [month, day, year] = datePart.split("-");
  const [hours = "0", minutes = "0", seconds = "0"] = timePart.split(":");
  const utcDate = new Date(
    Date.UTC(year, Number(month) - 1, day, hours, minutes, seconds),
  );

  if (Number.isNaN(utcDate.getTime())) {
    return {
      formattedDate: dateTimeString,
      formattedTime: "",
      timeZoneName: "",
    };
  }

  const userDate = new Date(utcDate.toLocaleString());
  const timeZoneLabel = userDate
    .toLocaleString("en", { timeZoneName: "short" })
    .split(" ")
    .pop();
  const paddedMonth = String(userDate.getMonth() + 1).padStart(2, "0");
  const paddedDay = String(userDate.getDate()).padStart(2, "0");
  const paddedYear = String(userDate.getFullYear());

  return {
    formattedDate: `${paddedMonth}-${paddedDay}-${paddedYear}`,
    formattedTime: userDate.toLocaleTimeString(),
    timeZoneName: timeZoneLabel || "",
  };
}

function normalizeNotificationRow(row = [], index = 0) {
  const timeInfo = formatNotificationTimestamp(row?.[6]);
  const formattedTimestamp = [
    timeInfo.formattedDate,
    timeInfo.formattedTime,
    timeInfo.timeZoneName,
  ]
    .filter((value) => String(value || "").trim() !== "")
    .join(" • ");
  const normalizedKey = [
    row?.[0],
    row?.[1],
    row?.[2],
    row?.[3],
    row?.[5],
    row?.[6],
    row?.[7],
    index,
  ]
    .filter((value) => String(value || "").trim() !== "")
    .join("__");

  return {
    key: normalizedKey || String(index),
    raw: row,
    submittalTitle: row?.[3] || row?.[1] || "Untitled Submittal",
    submittalNumber: row?.[2] || "—",
    person: row?.[5] || "Unknown User",
    action: row?.[7] || "No action details",
    filterableTitle: row?.[1] || row?.[3] || "",
    filterableNumber: row?.[2] || "",
    filterablePerson: row?.[5] || "",
    filterableAction: row?.[7] || "",
    formattedTimestamp,
    ...timeInfo,
  };
}

const FILTER_FIELDS = [
  { value: "submittalTitle", label: "Submittal Title" },
  { value: "submittalNumber", label: "Submittal Number" },
  { value: "person", label: "Person" },
  { value: "action", label: "Action" },
];
const PER_PAGE = 100;
const PERSON_COLUMN_WIDTH = 100;
const SUBMITTAL_NUMBER_COLUMN_WIDTH = 160;
const ACTION_COLUMN_WIDTH = 300;
const DEFAULT_TITLE_WIDTH = 260;
const MIN_TITLE_WIDTH = 200;
const MAX_TITLE_WIDTH = 520;
const FIXED_COLUMNS_WIDTH =
  PERSON_COLUMN_WIDTH +
  SUBMITTAL_NUMBER_COLUMN_WIDTH +
  ACTION_COLUMN_WIDTH;

const clampTitleWidth = (width) =>
  Math.min(MAX_TITLE_WIDTH, Math.max(MIN_TITLE_WIDTH, width));

const ResizableHeaderCell = ({
  resizable,
  width,
  onResize,
  onReset,
  children,
  ...restProps
}) => {
  const cleanupRef = useRef(() => {});

  useEffect(
    () => () => {
      cleanupRef.current();
    },
    [],
  );

  const startResize = (event) => {
    if (!resizable) return;
    event.preventDefault();
    event.stopPropagation();
    cleanupRef.current();

    const startX = event.clientX;
    const startWidth = width;
    const previousCursor = document.body.style.cursor;
    const previousUserSelect = document.body.style.userSelect;

    const handleMouseMove = (moveEvent) => {
      onResize(clampTitleWidth(startWidth + moveEvent.clientX - startX));
    };
    const handleMouseUp = () => cleanupRef.current();
    const cleanup = () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = previousCursor;
      document.body.style.userSelect = previousUserSelect;
      cleanupRef.current = () => {};
    };

    cleanupRef.current = cleanup;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  const resizeWithKeyboard = (event) => {
    if (!resizable || !["ArrowLeft", "ArrowRight"].includes(event.key))
      return;
    event.preventDefault();
    onResize(
      clampTitleWidth(width + (event.key === "ArrowRight" ? 20 : -20)),
    );
  };

  return (
    <th {...restProps} style={{ ...restProps.style, width }}>
      {children}
      {resizable && (
        <span
          className={styles.resizeHandle}
          role="separator"
          aria-label="Resize Submittal Title column"
          aria-orientation="vertical"
          aria-valuemin={MIN_TITLE_WIDTH}
          aria-valuemax={MAX_TITLE_WIDTH}
          aria-valuenow={width}
          tabIndex={0}
          onMouseDown={startResize}
          onKeyDown={resizeWithKeyboard}
          onDoubleClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            onReset();
          }}
        />
      )}
    </th>
  );
};

const NotificationTable = ({ company_id, project_id }) => {
  const [allActions, setAllActions] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedFields, setSelectedFields] = useState([]);
  const [selectedValues, setSelectedValues] = useState([]);
  const [titleColumnWidth, setTitleColumnWidth] = useState(
    DEFAULT_TITLE_WIDTH,
  );

  const code = AuthStorage.getItem("token");
  const email = AuthStorage.getItem("email");

  useEffect(() => {
    let isActive = true;

    if (!company_id || !project_id) {
      setAllActions([]);
      setIsLoading(false);
      return () => {
        isActive = false;
      };
    }

    setIsLoading(true);

    const data = {
      code,
      loginEmail: email,
      project_id,
      company_id,
      Page: 1,
      Per_Page: 20000,
    };

    SideBarEndService.NotificationCenter(data)
      .then((res) => {
        if (!isActive) {
          return;
        }

        setAllActions(res?.activityTracker || []);
      })
      .catch((err) => {
        console.log(err, "err");
        if (isActive) {
          setAllActions([]);
        }
      })
      .finally(() => {
        if (isActive) {
          setIsLoading(false);
        }
      });

    return () => {
      isActive = false;
    };
  }, [code, company_id, email, project_id]);

  const normalizedRows = useMemo(
    () => allActions.map((row, index) => normalizeNotificationRow(row, index)),
    [allActions],
  );

  const valueOptions = useMemo(() => {
    if (!selectedFields.length) {
      return [];
    }

    return selectedFields.map((field) => {
      const fieldMeta = FILTER_FIELDS.find((item) => item.value === field);
      const values = [
        ...new Set(
          normalizedRows
            .map((row) => row?.[field])
            .filter((value) => String(value || "").trim() !== ""),
        ),
      ].sort((left, right) =>
        String(left).localeCompare(String(right), undefined, {
          sensitivity: "base",
          numeric: true,
        }),
      );

      return {
        label: fieldMeta?.label || field,
        options: values.map((value) => ({
          value: `${field}::${value}`,
          label: value,
        })),
      };
    });
  }, [normalizedRows, selectedFields]);

  const filteredRows = useMemo(() => {
    if (!selectedValues.length) {
      return normalizedRows;
    }

    const groupedSelections = selectedValues.reduce((accumulator, token) => {
      const [field, value] = String(token).split("::");

      if (!field) {
        return accumulator;
      }

      if (!accumulator[field]) {
        accumulator[field] = new Set();
      }

      accumulator[field].add(value);
      return accumulator;
    }, {});

    return normalizedRows.filter((row) =>
      Object.entries(groupedSelections).every(([field, values]) =>
        values.has(String(row?.[field] || "")),
      ),
    );
  }, [normalizedRows, selectedValues]);

  const totalItems = filteredRows.length;
  const paginatedRows = useMemo(() => {
    const startIndex = (currentPage - 1) * PER_PAGE;
    return filteredRows.slice(startIndex, startIndex + PER_PAGE);
  }, [currentPage, filteredRows]);
  const tableRows = useMemo(
    () =>
      paginatedRows.map((row, index) => ({
        ...row,
        tableRenderKey: `${row.key}__page-${currentPage}__slot-${index}`,
      })),
    [currentPage, paginatedRows],
  );
  const tableInstanceKey = useMemo(
    () =>
      [
        currentPage,
        totalItems,
        selectedFields.join("|"),
        selectedValues.join("|"),
      ].join("__"),
    [currentPage, selectedFields, selectedValues, totalItems],
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedFields, selectedValues]);

  useEffect(() => {
    const maxPages = Math.max(1, Math.ceil(totalItems / PER_PAGE));

    if (currentPage > maxPages) {
      setCurrentPage(maxPages);
    }
  }, [currentPage, totalItems]);

  const columns = useMemo(
    () => [
      {
        title: "Person",
        dataIndex: "person",
        key: "person",
        width: PERSON_COLUMN_WIDTH,
        align: "center",
        render: (value) => (
          <Tooltip title={value} placement="top">
            <div className={styles.personCell}>
              <span className={styles.personAvatar}>{getInitials(value)}</span>
            </div>
          </Tooltip>
        ),
      },
      {
        title: "Submittal Title",
        dataIndex: "submittalTitle",
        key: "submittalTitle",
        width: titleColumnWidth,
        className: styles.titleColumn,
        ellipsis: true,
        onHeaderCell: () => ({
          width: titleColumnWidth,
          resizable: true,
          onResize: setTitleColumnWidth,
          onReset: () => setTitleColumnWidth(DEFAULT_TITLE_WIDTH),
        }),
        render: (value, record) => (
          <div className={styles.titleCell}>
            <Tooltip title={value} placement="top">
              <span className={styles.primaryText}>{value}</span>
            </Tooltip>
            {record.formattedTimestamp ? (
              <span className={styles.titleTimestamp}>
                {record.formattedTimestamp}
              </span>
            ) : null}
          </div>
        ),
      },
      {
        title: "Submittal Number",
        dataIndex: "submittalNumber",
        key: "submittalNumber",
        width: SUBMITTAL_NUMBER_COLUMN_WIDTH,
        render: (value) => (
          <Tooltip title={value} placement="top">
            <span className={styles.codeBadge}>{value}</span>
          </Tooltip>
        ),
      },
      {
        title: "Action",
        dataIndex: "action",
        key: "action",
        width: ACTION_COLUMN_WIDTH,
        render: (value) => (
          <Tooltip title={value} placement="topLeft">
            <span className={styles.actionText}>{value}</span>
          </Tooltip>
        ),
      },
    ],
    [titleColumnWidth],
  );

  const startIndex = totalItems === 0 ? 0 : (currentPage - 1) * PER_PAGE + 1;
  const endIndex = Math.min(currentPage * PER_PAGE, totalItems);

  return (
    <div className={styles.wrapper}>
      <div className={styles.toolbar}>
        <div className={styles.toolbarTitleBlock}>
          <span className={styles.toolbarEyebrow}>Activity Feed</span>
          {/* <span className={styles.toolbarTitle}>
            Recent procurement notifications
          </span> */}
        </div>

        <div className={styles.toolbarFilters}>
          <Select
            mode="multiple"
            allowClear
            value={selectedFields}
            placeholder="Filter fields"
            className={styles.filterSelect}
            optionFilterProp="label"
            onChange={(values) => {
              setSelectedFields(values);
              setSelectedValues((previousValues) =>
                previousValues.filter((token) =>
                  values.includes(String(token).split("::")[0]),
                ),
              );
            }}
            options={FILTER_FIELDS}
            maxTagCount={1}
          />

          <Select
            mode="multiple"
            allowClear
            disabled={!selectedFields.length}
            value={selectedValues}
            placeholder="Filter values"
            className={styles.filterSelect}
            optionFilterProp="label"
            onChange={setSelectedValues}
            options={valueOptions}
            maxTagCount={1}
          />
        </div>
      </div>

      {!isLoading && totalItems > 0 ? (
        <div className={styles.scrollHint} role="note">
          <SwapOutlined aria-hidden="true" />
          <span>Scroll horizontally · Drag the title column edge to resize</span>
        </div>
      ) : null}

      <div className={styles.tableCard}>
        {isLoading ? (
          <div className={styles.loadingState}>
            <LoadingComponent />
          </div>
        ) : totalItems === 0 ? (
          <div className={styles.emptyState}>
            <Empty
              description={
                selectedValues.length
                  ? "No notifications match the selected filters."
                  : "No procurement notifications yet."
              }
            />
          </div>
        ) : (
          <>
            <Table
              key={tableInstanceKey}
              columns={columns}
              dataSource={tableRows}
              pagination={false}
              rowKey="tableRenderKey"
              className={styles.notificationTable}
              size="small"
              tableLayout="fixed"
              components={{
                header: { cell: ResizableHeaderCell },
              }}
              scroll={{ x: FIXED_COLUMNS_WIDTH + titleColumnWidth }}
            />

            <div className={styles.footer}>
              <small className={styles.footerSummary}>
                Showing {startIndex}-{endIndex} of {totalItems} notifications
              </small>

              <div className={styles.footerControls}>
                <Pagination
                  current={currentPage}
                  total={totalItems}
                  pageSize={PER_PAGE}
                  showSizeChanger={false}
                  onChange={setCurrentPage}
                />
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default NotificationTable;
